import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Button, FormContainer, FormSection } from '../../components/aip';
import { DiarizeModal } from '../../components/DiarizeModal';
import { TranscriptionSegmentsModal } from '../../components/TranscriptionSegmentsModal';
import {
  getMinutesDetail,
  getMinutesAudioUrl,
  transcribeDiarize,
  getTranscriptionSegments,
  type MeetingMinutesDetail,
  type Speaker,
  type DiarizedSegment,
  type TranscriptionSegment,
} from '../../services/manageNewApi';

/**
 * 議事録詳細ページ
 */
export const MinutesDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const minutesId = id ? parseInt(id, 10) : null;

  // データ状態
  const [minutes, setMinutes] = useState<MeetingMinutesDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 話者分離モーダル状態
  const [diarizeModalOpen, setDiarizeModalOpen] = useState(false);
  const [diarizeLoading, setDiarizeLoading] = useState(false);
  const [diarizeSpeakers, setDiarizeSpeakers] = useState<Speaker[]>([]);
  const [diarizeSegments, setDiarizeSegments] = useState<DiarizedSegment[]>([]);

  // 録音一覧モーダル状態
  const [segmentsModalOpen, setSegmentsModalOpen] = useState(false);
  const [segmentsLoading, setSegmentsLoading] = useState(false);
  const [transcriptionSegments, setTranscriptionSegments] = useState<TranscriptionSegment[]>([]);

  // コピー状態
  const [copySuccess, setCopySuccess] = useState(false);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      if (!minutesId) {
        setError('IDが指定されていません');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await getMinutesDetail(minutesId);
        setMinutes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [minutesId]);

  // 日付フォーマット関数
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 話者分離ボタンクリック
  const handleDiarizeClick = async () => {
    if (!minutesId) return;

    setDiarizeLoading(true);
    try {
      const result = await transcribeDiarize(minutesId);
      setDiarizeSpeakers(result.speakers);
      setDiarizeSegments(result.segments);
      setDiarizeModalOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : '話者分離に失敗しました';
      alert(message);
    } finally {
      setDiarizeLoading(false);
    }
  };

  // 話者名更新時のコールバック
  const handleSpeakerNameUpdated = (speakerId: number, name: string) => {
    setDiarizeSpeakers(prev =>
      prev.map(s =>
        s.speaker_id === speakerId ? { ...s, speaker_name: name || null } : s
      )
    );
    setDiarizeSegments(prev =>
      prev.map(seg =>
        seg.speaker_id === speakerId ? { ...seg, speaker_name: name || null } : seg
      )
    );
  };

  // 議事録をクリップボードにコピー
  const handleCopyMinutes = async () => {
    if (!minutes?.minutes_markdown) return;

    try {
      await navigator.clipboard.writeText(minutes.minutes_markdown);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('コピーに失敗しました:', err);
      alert('クリップボードへのコピーに失敗しました');
    }
  };

  // 録音一覧ボタンクリック
  const handleSegmentsClick = async () => {
    if (!minutesId) return;

    setSegmentsLoading(true);
    try {
      const segments = await getTranscriptionSegments(minutesId);
      setTranscriptionSegments(segments);
      setSegmentsModalOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : '録音一覧の取得に失敗しました';
      alert(message);
    } finally {
      setSegmentsLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  if (!minutes && !loading) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: 'var(--color-red50)',
          color: 'var(--color-alert)',
          borderRadius: '0.5rem',
        }}
      >
        {error || '議事録が見つかりません'}
      </div>
    );
  }

  const audioUrl = minutesId ? getMinutesAudioUrl(minutesId) : null;
  const hasAudioFile = !!minutes?.server_file_path;

  return (
    <div style={{ width: '100%' }}>
      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-red50)',
            color: 'var(--color-alert)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <FormContainer>
        {/* 基本情報 */}
        <FormSection title="基本情報" layout="vertical">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-tertiary)',
                  marginBottom: '0.25rem',
                }}
              >
                会議名
              </div>
              <div style={{ fontWeight: 500 }}>
                {minutes?.minutes_meeting_name || '（名称未設定）'}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-tertiary)',
                  marginBottom: '0.25rem',
                }}
              >
                議事録生成日時
              </div>
              <div>{formatDate(minutes?.minutes_generated_at || null)}</div>
            </div>
          </div>

          {minutes?.minutes_meeting_goal && (
            <div style={{ marginTop: '1rem' }}>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-tertiary)',
                  marginBottom: '0.25rem',
                }}
              >
                目的・ゴール
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{minutes.minutes_meeting_goal}</div>
            </div>
          )}
        </FormSection>

        {/* 音声再生 */}
        <FormSection title="録音音声">
          {/* 録音一覧ボタン（セクション右上） */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '0.5rem',
              marginTop: '-0.25rem',
            }}
          >
            <Button
              buttonType="outline"
              size="sm"
              onClick={handleSegmentsClick}
              disabled={segmentsLoading}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>list</span>
              {segmentsLoading ? '読込中...' : '録音一覧'}
            </Button>
          </div>
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--color-gray50)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            {!hasAudioFile ? (
              <div
                style={{
                  color: 'var(--text-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <span className="material-symbols-outlined" style={{ flexShrink: 0 }}>info</span>
                音声ファイルは未登録です
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <audio
                  controls
                  preload="metadata"
                  style={{ width: '100%' }}
                >
                  <source src={audioUrl!} type="audio/wav" />
                  お使いのブラウザは音声再生に対応していません。
                </audio>
                <div>
                  <Button
                    buttonType="outline"
                    size="md"
                    onClick={handleDiarizeClick}
                    disabled={diarizeLoading}
                  >
                    <span className="material-symbols-outlined">record_voice_over</span>
                    {diarizeLoading ? '処理中...' : '文字起こし（話者分離）'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </FormSection>

        {/* 議事録内容 */}
        <FormSection title="議事録" layout="vertical">
          <div style={{ position: 'relative' }}>
            {/* コピーボタン */}
            {minutes?.minutes_markdown && (
              <button
                type="button"
                onClick={handleCopyMinutes}
                title="議事録をクリップボードにコピー"
                style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  zIndex: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: copySuccess ? 'var(--color-green50)' : 'var(--color-gray100)',
                  border: `1px solid ${copySuccess ? 'var(--color-green200)' : 'var(--color-gray300)'}`,
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  color: copySuccess ? 'var(--color-green700)' : 'var(--text-secondary)',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!copySuccess) {
                    e.currentTarget.style.backgroundColor = 'var(--color-gray200)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!copySuccess) {
                    e.currentTarget.style.backgroundColor = 'var(--color-gray100)';
                  }
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: '1rem' }}
                >
                  {copySuccess ? 'check' : 'content_copy'}
                </span>
                {copySuccess ? 'コピーしました' : 'コピー'}
              </button>
            )}
            <div
              className="markdown-content"
              data-column="full"
              style={{
                width: '100%',
                maxWidth: '100%',
                padding: '1.5rem',
                paddingTop: minutes?.minutes_markdown ? '3rem' : '1.5rem',
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-gray200)',
                borderRadius: 'var(--radius-md)',
                lineHeight: 1.8,
                boxSizing: 'border-box',
              }}
            >
            {minutes?.minutes_markdown ? (
              <ReactMarkdown
                components={{
                  h1: ({ children }) => (
                    <h1
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        marginTop: '1.5rem',
                        marginBottom: '0.75rem',
                        paddingBottom: '0.5rem',
                        borderBottom: '2px solid var(--color-gray200)',
                      }}
                    >
                      {children}
                    </h1>
                  ),
                  h2: ({ children }) => (
                    <h2
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 600,
                        marginTop: '1.25rem',
                        marginBottom: '0.5rem',
                        paddingBottom: '0.25rem',
                        borderBottom: '1px solid var(--color-gray200)',
                      }}
                    >
                      {children}
                    </h2>
                  ),
                  h3: ({ children }) => (
                    <h3
                      style={{
                        fontSize: '1.1rem',
                        fontWeight: 600,
                        marginTop: '1rem',
                        marginBottom: '0.5rem',
                      }}
                    >
                      {children}
                    </h3>
                  ),
                  p: ({ children }) => (
                    <p style={{ marginBottom: '0.75rem' }}>{children}</p>
                  ),
                  ul: ({ children }) => (
                    <ul
                      style={{
                        marginBottom: '0.75rem',
                        paddingLeft: '1.5rem',
                        listStyleType: 'disc',
                      }}
                    >
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol
                      style={{
                        marginBottom: '0.75rem',
                        paddingLeft: '1.5rem',
                        listStyleType: 'decimal',
                      }}
                    >
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li style={{ marginBottom: '0.25rem' }}>{children}</li>
                  ),
                  strong: ({ children }) => (
                    <strong style={{ fontWeight: 600 }}>{children}</strong>
                  ),
                  hr: () => (
                    <hr
                      style={{
                        border: 'none',
                        borderTop: '1px solid var(--color-gray200)',
                        margin: '1.5rem 0',
                      }}
                    />
                  ),
                  blockquote: ({ children }) => (
                    <blockquote
                      style={{
                        borderLeft: '4px solid var(--color-gray300)',
                        paddingLeft: '1rem',
                        margin: '1rem 0',
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic',
                      }}
                    >
                      {children}
                    </blockquote>
                  ),
                  code: ({ children }) => (
                    <code
                      style={{
                        backgroundColor: 'var(--color-gray100)',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.875rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {children}
                    </code>
                  ),
                  pre: ({ children }) => (
                    <pre
                      style={{
                        backgroundColor: 'var(--color-gray100)',
                        padding: '1rem',
                        borderRadius: '0.5rem',
                        overflow: 'auto',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                        marginBottom: '1rem',
                      }}
                    >
                      {children}
                    </pre>
                  ),
                }}
              >
                {minutes.minutes_markdown}
              </ReactMarkdown>
            ) : (
              <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '2rem' }}>
                議事録の内容がありません
              </div>
            )}
            </div>
          </div>
        </FormSection>

        {/* フッターボタン */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-start', marginTop: '1rem' }}>
          <Button
            buttonType="outline"
            size="md"
            type="button"
            onClick={() => navigate('/meeting/manage-new/minutes')}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            一覧に戻る
          </Button>
        </div>
      </FormContainer>

      {/* 話者分離モーダル */}
      {minutesId && (
        <DiarizeModal
          open={diarizeModalOpen}
          onClose={() => setDiarizeModalOpen(false)}
          minutesId={minutesId}
          speakers={diarizeSpeakers}
          segments={diarizeSegments}
          onSpeakerNameUpdated={handleSpeakerNameUpdated}
        />
      )}

      {/* 録音一覧モーダル */}
      {minutesId && (
        <TranscriptionSegmentsModal
          open={segmentsModalOpen}
          onClose={() => setSegmentsModalOpen(false)}
          minutesId={minutesId}
          segments={transcriptionSegments}
        />
      )}
    </div>
  );
};

export default MinutesDetailPage;

